import { BarItemProps, Bar } from '@nivo/bar'
import { useContext, useEffect, useState } from 'react'
import { ReportingContext } from '../../../../contexts/reporting-context'
import { ApiContext } from '../../../../contexts/api-context'
import { SeatBooking } from '../../../../types/andTypes'
import { TeamExtensions } from '../../../../constants'
import { useTooltip } from '@nivo/tooltip'
import { ToolTip, ChartBlock, ChartLabel } from '../reporting-charts-styles'
import { AxisTickProps } from '@nivo/axes'
import useElementSize from '../../../../utilities/useElementSize'
import useTotalWorkingDays from '../../../../hooks/useTotalWorkingDays'
import dayjs from 'dayjs'

type ChartData = {
  id: string
  label: string
  value: number
}

const calculateTextWidth = (
  value: string,
  font = 'normal 400 12px Poppins',
  canvas: HTMLCanvasElement = document.createElement('canvas')
) => {
  const context = canvas.getContext('2d')
  if (context) {
    context.font = font
    const metrics = context.measureText(value)
    return metrics.width
  }
  return 0
}

export const DeskBookingByTeams = () => {
  const { reportSeatBookingsByBU, reportBusinessUnit, reportDateRange, firstBookedSeat } =
    useContext(ReportingContext)
  const { getANDis } = useContext(ApiContext)
  const registeredANDis = getANDis()

  const start =
    reportDateRange?.id !== 'start-to-date'
      ? reportDateRange?.startDate
      : dayjs(firstBookedSeat?.dateBooked)
  const workingDays = useTotalWorkingDays(start, reportDateRange?.endDate)

  const [bookingData, setBookingData] = useState<ChartData[]>([])
  const [chartRef, { width: chartWidth }] = useElementSize()

  useEffect(() => {
    constructBookingByTeams(reportSeatBookingsByBU)
  }, [reportSeatBookingsByBU, registeredANDis, workingDays])

  const sortTeams = (teams: Record<string, Record<string, number>>) =>
    Object.keys(teams)
      .sort((a, b) => {
        if (a.toLowerCase() > b.toLowerCase()) {
          return -1
        }
        if (a.toLowerCase() < b.toLowerCase()) {
          return 1
        }
        return 0
      })
      .reduce((acc, key) => {
        acc[key] = teams[key]

        return acc
      }, {} as Record<string, Record<string, number>>)

  const constructBookingByTeams = (reportSeatBookings: SeatBooking[]) => {
    const teams = [
      ...(reportBusinessUnit?.teams || []),
      TeamExtensions.OTHER_BUSINESS_UNIT,
      TeamExtensions.UNDEFINED_TEAM
    ]

    const getTeamFromId = (teamId: string) => teams.find(team => team.id === teamId)

    let daySeatsMap =
      teams.reduce((allTeams, team) => {
        allTeams[`${team.name}`] = { bookedSeats: 0, totalSeats: 0 }
        return allTeams
      }, {} as Record<string, Record<string, number>>) || {}

    daySeatsMap = sortTeams(daySeatsMap)

    reportSeatBookings.forEach(data => {
      data.seats.forEach(seat => {
        if (seat?.teamId) {
          const teamName = getTeamFromId(seat?.teamId)?.name
          if (teamName) {
            daySeatsMap[teamName].bookedSeats =
              daySeatsMap[teamName] !== undefined ? daySeatsMap[teamName].bookedSeats + 1 : 1
          }
        }
      })
    })

    registeredANDis.forEach(andi => {
      if (andi.team) {
        const teamName = getTeamFromId(andi.team)?.name
        if (reportBusinessUnit) {
          if (teamName) {
            daySeatsMap[teamName].totalSeats =
              daySeatsMap[teamName] !== undefined
                ? daySeatsMap[teamName].totalSeats + workingDays
                : workingDays
          } else {
            daySeatsMap[TeamExtensions.UNDEFINED_TEAM.name].totalSeats =
              daySeatsMap[TeamExtensions.UNDEFINED_TEAM.name] !== undefined
                ? daySeatsMap[TeamExtensions.UNDEFINED_TEAM.name].totalSeats + workingDays
                : workingDays
          }
        }
      }
    })

    const chartData: ChartData[] = []
    if (reportBusinessUnit) {
      Object.keys(daySeatsMap).forEach(team => {
        chartData.push({
          id: team,
          label: team,
          value:
            daySeatsMap[team].totalSeats === 0
              ? 0
              : Math.round((daySeatsMap[team].bookedSeats / daySeatsMap[team].totalSeats) * 100)
        })
      })
      setBookingData(chartData)
    }
  }

  const getTickValues = (data: ChartData[]) => {
    const maxSeats = Math.max(...data.map(({ value }) => value))
    switch (maxSeats) {
      case 0:
        return []
      case 1:
        return [0, 1]
      case 2:
        return [0, 1, 2]
      case 3:
        return [0, 1, 2, 3]
      default:
        return 4
    }
  }

  const truncateTeam = (team: string) => {
    const maxWidth = 130 // px
    let truncatedTeam = team.trim()
    const canvas = document.createElement('canvas')
    if (calculateTextWidth(truncatedTeam, 'normal 400 12px Poppins', canvas) < maxWidth) {
      return truncatedTeam
    }
    while (
      calculateTextWidth(truncatedTeam + '...', 'normal 400 12px Poppins', canvas) > maxWidth
    ) {
      truncatedTeam = truncatedTeam.slice(0, truncatedTeam.length - 1).trim()
    }
    return truncatedTeam + '...'
  }

  const LeftAlignedAxisText = (props: AxisTickProps<string>) => {
    return (
      <g>
        <text
          x={props.x - 130}
          y={props.y}
          textAnchor="start"
          dominantBaseline="central"
          fill="black"
          fontFamily="Poppins"
          fontSize={12}
          data-testid={`team-chart-axis-label-${props.value}`}
        >
          {truncateTeam(props.value)}
        </text>
      </g>
    )
  }

  const BarComponent = ({ bar }: BarItemProps<{ id: string; label: string; value: number }>) => {
    const { showTooltipFromEvent, hideTooltip } = useTooltip()
    if (bar.data.value === 0) return null
    return (
      <g
        data-testid={`team-chart-bar-${bar.key}-${bar.data.value}`}
        transform={`translate(${0},${bar?.y || 0})`}
        onMouseMove={(event): void => {
          showTooltipFromEvent(
            <ToolTip>
              {bar.data.indexValue}: {bar.data.value}
            </ToolTip>,
            event
          )
        }}
        onMouseLeave={(): void => {
          hideTooltip()
        }}
      >
        <rect width={bar.width} height={bar.height} fill={bar.color} />
      </g>
    )
  }

  // allow more chart height when there aren't many bars to display
  // otherwise, the chart looks too compressed
  const heightOffset = bookingData.length <= 10 ? 45 / Math.log1p(bookingData.length) : 0

  return (
    <ChartBlock ref={chartRef}>
      <ChartLabel>Desk bookings by team:</ChartLabel>
      <Bar
        data={bookingData}
        margin={{ top: 15, right: 15, bottom: 60, left: 150 }}
        padding={0.3}
        layout="horizontal"
        enableLabel={true}
        labelTextColor="white"
        enableGridX={false}
        enableGridY={false}
        maxValue={100}
        colors={['#D82036']}
        axisLeft={{
          renderTick: props => LeftAlignedAxisText(props)
        }}
        axisBottom={{
          tickValues: getTickValues(bookingData),
          tickSize: 0,
          tickPadding: 5,
          legend: 'Percentage %',
          legendPosition: 'middle',
          legendOffset: 35
        }}
        theme={{
          fontSize: 12,
          fontFamily: 'Poppins',
          axis: {
            domain: {
              line: {
                stroke: 'lightgrey',
                strokeWidth: 1
              }
            }
          }
        }}
        barComponent={BarComponent}
        height={bookingData.length * 35 + heightOffset}
        width={chartWidth >= 20 ? chartWidth - 20 : 0}
      />
    </ChartBlock>
  )
}
